import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Server, 
  Shield, 
  Zap, 
  Globe, 
  Check, 
  Moon, 
  Sun, 
  User, 
  LogOut, 
  CreditCard,
  LayoutDashboard,
  Menu,
  X,
  ArrowRight,
  Database,
  Clock,
  Headphones,
  Users,
  PoundSterling,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  Trash2,
  ChevronRight,
  Settings,
  Crown,
  HardDrive,
  Link,
  Copy,
  ExternalLink,
  Rocket,
  ArrowDownToLine,
  FileText,
  Send,
  BookOpen,
  MessageCircle,
  Tag,
  Ticket,
  Plus,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || "dark";
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Navigation Component
const Navigation = () => {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate("/")}
            data-testid="logo-link"
          >
            <Server className="h-8 w-8 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight">Frantronics Hosting</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <a href="#plans" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Plans
            </a>
            <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Features
            </a>
            
            <div className="flex items-center gap-2">
              <Sun className="h-4 w-4 text-muted-foreground" />
              <Switch
                checked={theme === "dark"}
                onCheckedChange={toggleTheme}
                data-testid="theme-toggle"
              />
              <Moon className="h-4 w-4 text-muted-foreground" />
            </div>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2" data-testid="user-menu-trigger">
                    <User className="h-4 w-4" />
                    {user.name}
                    {isAdmin && <Crown className="h-3 w-3 text-yellow-500" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="dashboard-menu-item">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-menu-item">
                      <Settings className="h-4 w-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="login-nav-btn">
                  Login
                </Button>
                <Button size="sm" onClick={() => navigate("/register")} data-testid="register-nav-btn">
                  Get Started
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Sun className="h-3 w-3 text-muted-foreground" />
              <Switch
                checked={theme === "dark"}
                onCheckedChange={toggleTheme}
                className="scale-75"
                data-testid="theme-toggle-mobile"
              />
              <Moon className="h-3 w-3 text-muted-foreground" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-2">
              <a href="#plans" className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                Plans
              </a>
              <a href="#features" className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                Features
              </a>
              <Separator className="my-2" />
              {user ? (
                <>
                  <Button variant="ghost" className="justify-start" onClick={() => navigate("/dashboard")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" className="justify-start" onClick={() => navigate("/admin")}>
                      <Settings className="h-4 w-4 mr-2" />
                      Admin Panel
                    </Button>
                  )}
                  <Button variant="ghost" className="justify-start text-destructive" onClick={logout}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" className="justify-start" onClick={() => navigate("/login")}>
                    Login
                  </Button>
                  <Button className="justify-start" onClick={() => navigate("/register")}>
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Landing Page
const LandingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingAnnual, setBillingAnnual] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/plans`);
        setPlans(response.data);
      } catch (error) {
        console.error("Error fetching plans:", error);
        toast.error("Failed to load pricing plans");
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSelectPlan = (planId) => {
    const billingParam = billingAnnual ? "?billing=annual" : "";
    if (!user) {
      navigate("/register", { state: { selectedPlan: planId, billing: billingAnnual ? "annual" : "monthly" } });
    } else {
      navigate(`/checkout/${planId}${billingParam}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.15)_0%,rgba(0,0,0,0)_70%)]" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <Badge variant="outline" className="mb-6 px-4 py-1.5 text-sm font-medium border-primary/30 text-primary">
            UK-Based Hosting Provider
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            Lightning-Fast Web Hosting
            <span className="block text-primary mt-2">Built for Scale</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Affordable hosting with a powerful dashboard and real human support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="gap-2 px-8 py-6 text-base font-medium"
              onClick={() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="view-plans-btn"
            >
              View Plans <ArrowRight className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="px-8 py-6 text-base font-medium"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="learn-more-btn"
            >
              Learn More
            </Button>
          </div>
        </div>
        
        {/* Background Image */}
        <div className="absolute inset-0 -z-10 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1680992046626-418f7e910589"
            alt="Server Infrastructure"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-surface-light dark:bg-surface-dark">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold tracking-tight mb-4">
              Why Choose Frantronics Hosting?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Powerful dashboard control with real human support. Built for UK businesses.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-card/50 backdrop-blur border-border/50 hover:border-primary/30 transition-colors" data-testid="feature-speed-card">
              <CardHeader>
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="font-heading text-lg">WordPress Optimised</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  1-click WordPress install, auto-updates, and optimised servers for blazing speed
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur border-border/50 hover:border-primary/30 transition-colors" data-testid="feature-security-card">
              <CardHeader>
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="font-heading text-lg">Enterprise Security</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Free SSL, DDoS protection, and automated security patches included
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur border-border/50 hover:border-primary/30 transition-colors" data-testid="feature-global-card">
              <CardHeader>
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="font-heading text-lg">UK Data Centres</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Lightning-fast UK servers with global CDN for worldwide performance
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur border-border/50 hover:border-primary/30 transition-colors" data-testid="feature-support-card">
              <CardHeader>
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                  <Headphones className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="font-heading text-lg">UK Human Support</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Real humans, not bots. UK-based support team available 24/7
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="plans" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold tracking-tight mb-4">
              Simple, Transparent Hosting — Built for UK Businesses
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Powerful dashboard control with real human support. No hidden fees. No stress.
            </p>
            
            {/* Annual/Monthly Toggle */}
            <div className="inline-flex items-center gap-3 bg-muted/60 rounded-full px-2 py-1.5 border border-border" data-testid="billing-toggle">
              <button
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${!billingAnnual ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setBillingAnnual(false)}
                data-testid="billing-monthly-btn"
              >
                Monthly
              </button>
              <button
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${billingAnnual ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setBillingAnnual(true)}
                data-testid="billing-annual-btn"
              >
                Yearly <span className="text-xs ml-1 opacity-90">(Save 20%)</span>
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-pulse text-primary">Loading plans...</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                {plans.map((plan) => {
                  const isHighlighted = plan.highlighted;
                  const monthlyPrice = billingAnnual ? (plan.annual_price / 12).toFixed(2) : plan.price;
                  const showIntro = !billingAnnual && plan.renewal_price && plan.price < plan.renewal_price;
                  
                  return (
                    <Card 
                      key={plan.id}
                      className={`relative transition-all duration-300 hover:scale-[1.02] flex flex-col ${
                        isHighlighted 
                          ? 'border-primary shadow-[0_0_25px_rgba(59,130,246,0.25)] scale-[1.02]' 
                          : plan.badge 
                            ? 'border-blue-500/40 hover:border-blue-500/60'
                            : 'border-border hover:border-primary/30'
                      }`}
                      data-testid={`plan-card-${plan.id}`}
                    >
                      {(plan.badge || isHighlighted) && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                          <Badge className={`px-4 ${isHighlighted ? 'bg-primary text-primary-foreground' : 'bg-blue-600 text-white'}`}>
                            {isHighlighted ? 'Most Popular' : plan.badge}
                          </Badge>
                        </div>
                      )}
                      <CardHeader className="pt-8 pb-4">
                        <CardTitle className="font-heading text-xl">{plan.name}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">{plan.tagline}</p>
                        <div className="mt-4">
                          <div className="flex items-baseline gap-1">
                            <span className="font-heading text-4xl font-bold">
                              £{billingAnnual ? monthlyPrice : plan.price}
                            </span>
                            <span className="text-muted-foreground">/month</span>
                          </div>
                          {billingAnnual && (
                            <p className="text-xs text-muted-foreground mt-1">
                              £{plan.annual_price}/year billed annually
                            </p>
                          )}
                          {showIntro && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Renews at £{plan.renewal_price}/month
                            </p>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4 flex-1">
                        <Separator />
                        <ul className="space-y-2.5">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        {plan.bonus && (
                          <div className="pt-2 border-t border-border">
                            <p className="text-sm font-semibold text-primary flex items-center gap-1.5">
                              <Zap className="h-3.5 w-3.5" />
                              Bonus: {plan.bonus}
                            </p>
                          </div>
                        )}
                      </CardContent>
                      <CardFooter className="flex-col gap-3 pt-2 pb-6">
                        <Button 
                          className="w-full"
                          variant={isHighlighted ? 'default' : plan.badge ? 'default' : 'outline'}
                          size="lg"
                          onClick={() => handleSelectPlan(plan.id)}
                          data-testid={`select-plan-${plan.id}-btn`}
                        >
                          {user ? 'Subscribe Now' : (plan.cta || 'Get Started')}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                          No setup fees · Cancel anytime
                        </p>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>
              
              {/* Value Strip */}
              <div className="mt-16 bg-muted/40 rounded-lg border border-border p-8">
                <h3 className="font-heading text-xl font-bold text-center mb-6">Why Choose Frantronics Hosting?</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="flex items-center gap-3 text-sm" data-testid="value-dashboard">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <LayoutDashboard className="h-4 w-4 text-primary" />
                    </div>
                    <span>Powerful Dashboard + Full Control</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm" data-testid="value-support">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Headphones className="h-4 w-4 text-primary" />
                    </div>
                    <span>Real UK-Based Human Support</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm" data-testid="value-migration">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <ArrowRight className="h-4 w-4 text-primary" />
                    </div>
                    <span>Free Website Migration</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm" data-testid="value-reliability">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-4 w-4 text-primary" />
                    </div>
                    <span>Built for Speed, Security & Reliability</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-muted-foreground mb-8">
            Join UK businesses who trust Frantronics Hosting. Launch your website today.
          </p>
          <Button 
            size="lg" 
            className="px-8"
            onClick={() => navigate(user ? "/dashboard" : "/register")}
            data-testid="cta-get-started-btn"
          >
            {user ? 'Go to Dashboard' : 'Get Started Now'}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Server className="h-6 w-6 text-primary" />
              <span className="font-heading font-bold">Frantronics Hosting</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-privacy-link">
                Privacy Policy
              </a>
              <p className="text-sm text-muted-foreground">
                © 2026 Frantronics Hosting. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      const from = location.state?.from || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      const from = location.state?.from || "/dashboard";
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Navigation />
      <Card className="w-full max-w-md" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Server className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="font-heading text-2xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to your Frantronics Hosting account</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email"
                type="email" 
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password"
                type="password" 
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Don't have an account?{" "}
              <span 
                className="text-primary cursor-pointer hover:underline"
                onClick={() => navigate("/register")}
                data-testid="go-to-register-link"
              >
                Sign up
              </span>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      const selectedPlan = location.state?.selectedPlan;
      if (selectedPlan) {
        navigate(`/checkout/${selectedPlan}`, { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created successfully!");
      const selectedPlan = location.state?.selectedPlan;
      if (selectedPlan) {
        navigate(`/checkout/${selectedPlan}`, { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Navigation />
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Server className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="font-heading text-2xl">Create Account</CardTitle>
          <CardDescription>Get started with Frantronics Hosting today</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input 
                id="name"
                type="text" 
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email"
                type="email" 
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password"
                type="password" 
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                data-testid="register-password-input"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
              {loading ? "Creating account..." : "Create Account"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Already have an account?{" "}
              <span 
                className="text-primary cursor-pointer hover:underline"
                onClick={() => navigate("/login")}
                data-testid="go-to-login-link"
              >
                Sign in
              </span>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const planId = window.location.pathname.split("/").pop();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const period = params.get("billing");
    if (period === "annual") setBillingPeriod("annual");
    
    const fetchPlan = async () => {
      try {
        const response = await axios.get(`${API}/plans/${planId}`);
        setPlan(response.data);
      } catch (error) {
        toast.error("Plan not found");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [planId, navigate]);

  const displayPrice = billingPeriod === "annual" ? plan?.annual_price : plan?.price;
  const priceLabel = billingPeriod === "annual" ? "/year" : "/month";

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/promo/validate`, { code: promoCode.trim(), plan_id: planId });
      setPromoApplied(response.data);
      toast.success(`Promo applied! ${response.data.discount_percent}% off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid promo code");
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoCode("");
  };

  const finalPrice = promoApplied ? promoApplied.discounted_price : displayPrice;

  const handleCheckout = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/checkout/create-session`,
        { plan_id: planId, origin_url: window.location.origin, promo_code: promoApplied?.code || null, billing_period: billingPeriod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout failed");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Navigation />
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 px-4">
      <Navigation />
      <div className="max-w-2xl mx-auto">
        <Card data-testid="checkout-card">
          <CardHeader>
            <CardTitle className="font-heading text-2xl flex items-center gap-2">
              <CreditCard className="h-6 w-6 text-primary" />
              Checkout
            </CardTitle>
            <CardDescription>Complete your subscription to {plan?.name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Billing Period Toggle */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
              <button
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${billingPeriod === 'monthly' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setBillingPeriod("monthly")}
                data-testid="checkout-monthly-btn"
              >
                Monthly
              </button>
              <button
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${billingPeriod === 'annual' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setBillingPeriod("annual")}
                data-testid="checkout-annual-btn"
              >
                Yearly (Save 20%)
              </button>
            </div>
            
            <div className="p-6 rounded-sm bg-muted/50 border border-border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold">{plan?.name} Plan</h3>
                  <p className="text-sm text-muted-foreground">{plan?.support} Support · {billingPeriod === 'annual' ? 'Annual' : 'Monthly'} Billing</p>
                </div>
                <Badge variant="outline">{plan?.storage}</Badge>
              </div>
              <Separator className="my-4" />
              <ul className="space-y-2 mb-4">
                {plan?.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Separator className="my-4" />
              <div className="space-y-2">
                {promoApplied && (
                  <>
                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                      <span>Original Price</span>
                      <span className="line-through">£{displayPrice}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-green-500">
                      <span className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        Discount ({promoApplied.discount_percent}% off)
                      </span>
                      <span>-£{(displayPrice - promoApplied.discounted_price).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">{billingPeriod === 'annual' ? 'Annual' : 'Monthly'} Total</span>
                  <span className="font-heading text-2xl font-bold">£{finalPrice}</span>
                </div>
                {billingPeriod === 'annual' && (
                  <p className="text-xs text-muted-foreground text-right">That's £{(finalPrice / 12).toFixed(2)}/month</p>
                )}
              </div>
            </div>

            {/* Promo Code */}
            <div className="space-y-2">
              <Label className="text-sm">Promo Code</Label>
              {promoApplied ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <Tag className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium text-green-500 flex-1">{promoApplied.code} — {promoApplied.discount_percent}% off</span>
                  <Button variant="ghost" size="sm" onClick={handleRemovePromo} data-testid="remove-promo-btn">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleApplyPromo()}
                    data-testid="promo-code-input"
                  />
                  <Button variant="outline" onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()} data-testid="apply-promo-btn">
                    {promoLoading ? "..." : "Apply"}
                  </Button>
                </div>
              )}
            </div>

            <div className="p-4 rounded-sm bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-primary" />
                <span>Secure payment powered by Stripe</span>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button 
              className="w-full" 
              size="lg"
              onClick={handleCheckout}
              disabled={processing}
              data-testid="proceed-to-payment-btn"
            >
              {processing ? "Processing..." : `Pay £${finalPrice}${billingPeriod === 'annual' ? '/year' : '/month'}`}
            </Button>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => navigate("/")}
              data-testid="back-to-plans-btn"
            >
              Back to Plans
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

// Checkout Success Page
const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [subscription, setSubscription] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId || !token) return;

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(
          `${API}/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.payment_status === "paid") {
          setStatus("success");
          setSubscription(response.data.subscription);
          return;
        } else if (response.data.status === "expired") {
          setStatus("expired");
          return;
        }

        attempts++;
        setTimeout(pollStatus, pollInterval);
      } catch (error) {
        console.error("Error checking status:", error);
        attempts++;
        setTimeout(pollStatus, pollInterval);
      }
    };

    pollStatus();
  }, [sessionId, token]);

  return (
    <div className="min-h-screen bg-background pt-24 px-4">
      <Navigation />
      <div className="max-w-md mx-auto">
        <Card className="text-center" data-testid="checkout-success-card">
          <CardHeader>
            {status === "checking" && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Clock className="h-8 w-8 text-primary animate-pulse" />
                </div>
                <CardTitle className="font-heading">Processing Payment</CardTitle>
                <CardDescription>Please wait while we confirm your payment...</CardDescription>
              </>
            )}
            {status === "success" && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                  <Check className="h-8 w-8 text-green-500" />
                </div>
                <CardTitle className="font-heading text-green-500">Payment Successful!</CardTitle>
                <CardDescription>Your subscription is now active. A confirmation email has been sent.</CardDescription>
              </>
            )}
            {status === "expired" && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                  <X className="h-8 w-8 text-destructive" />
                </div>
                <CardTitle className="font-heading text-destructive">Session Expired</CardTitle>
                <CardDescription>Your payment session has expired</CardDescription>
              </>
            )}
            {status === "timeout" && (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mb-4">
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
                <CardTitle className="font-heading">Verification Taking Longer</CardTitle>
                <CardDescription>Please check your dashboard for the subscription status</CardDescription>
              </>
            )}
          </CardHeader>
          {subscription && (
            <CardContent>
              <div className="p-4 rounded-sm bg-muted/50 border border-border text-left">
                <p className="text-sm text-muted-foreground mb-2">Subscription Details</p>
                <p className="font-semibold">{subscription.plan_name} Plan</p>
                <p className="text-sm text-muted-foreground">£{subscription.price}{subscription.billing_period === 'annual' ? '/year' : '/month'}</p>
              </div>
            </CardContent>
          )}
          <CardFooter>
            <Button 
              className="w-full" 
              onClick={() => navigate("/dashboard")}
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

// Checkout Cancel Page
const CheckoutCancelPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pt-24 px-4">
      <Navigation />
      <div className="max-w-md mx-auto">
        <Card className="text-center" data-testid="checkout-cancel-card">
          <CardHeader>
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <X className="h-8 w-8 text-muted-foreground" />
            </div>
            <CardTitle className="font-heading">Payment Cancelled</CardTitle>
            <CardDescription>Your payment was not processed</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              No charges were made to your account. You can try again or choose a different plan.
            </p>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <Button 
              className="w-full" 
              onClick={() => navigate("/")}
              data-testid="back-to-pricing-btn"
            >
              Back to Pricing
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState({});
  const [hostingPackages, setHostingPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [provisionDialogOpen, setProvisionDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [domainName, setDomainName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("hosting");
  const [migrations, setMigrations] = useState([]);
  const [migrationGuide, setMigrationGuide] = useState(null);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [migrationForm, setMigrationForm] = useState({ source_url: "", source_host: "", ftp_host: "", ftp_username: "", ftp_password: "", notes: "" });
  const [tickets, setTickets] = useState([]);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: "", message: "", priority: "normal" });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState("");
  const [invoices, setInvoices] = useState([]);

  const fetchStats = async () => {
    try {
      const [statsRes, plansRes, hostingRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/plans`),
        axios.get(`${API}/hosting/packages`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      const plansMap = {};
      plansRes.data.forEach(p => { plansMap[p.id] = p; });
      setPlans(plansMap);
      const hostingMap = {};
      hostingRes.data.forEach(h => { hostingMap[h.subscription_id] = h; });
      setHostingPackages(hostingMap);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMigrations = async () => {
    try {
      const [migrationsRes, guideRes] = await Promise.all([
        axios.get(`${API}/migrations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/migrations/guide`)
      ]);
      setMigrations(migrationsRes.data);
      setMigrationGuide(guideRes.data);
    } catch (error) {
      console.error("Error fetching migrations:", error);
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      setTickets(res.data);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/invoices`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoices(res.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchMigrations();
    fetchTickets();
    fetchInvoices();
  }, [token]);

  const handleCancelClick = (subscription) => {
    setSelectedSubscription(subscription);
    setCancelDialogOpen(true);
  };

  const handleProvisionClick = (subscription) => {
    setSelectedSubscription(subscription);
    setDomainName("");
    setProvisionDialogOpen(true);
  };

  const handleProvisionHosting = async () => {
    if (!selectedSubscription || !domainName) return;
    setProcessing(true);
    try {
      await axios.post(
        `${API}/hosting/provision`,
        { subscription_id: selectedSubscription.id, domain: domainName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Hosting provisioned successfully! Check your email for credentials.");
      setProvisionDialogOpen(false);
      setDomainName("");
      setSelectedSubscription(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to provision hosting");
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleCancelConfirm = async () => {
    if (!selectedSubscription) return;
    setProcessing(true);
    try {
      await axios.post(
        `${API}/subscriptions/${selectedSubscription.id}/cancel`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Subscription cancelled. It will remain active until the end of your billing period.");
      setCancelDialogOpen(false);
      setCancelReason("");
      setSelectedSubscription(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setProcessing(false);
    }
  };

  const handleRenew = async (subscription) => {
    try {
      const response = await axios.post(
        `${API}/subscriptions/${subscription.id}/renew`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to renew subscription");
    }
  };

  const handleSubmitMigration = async () => {
    if (!migrationForm.source_url) return;
    setProcessing(true);
    try {
      await axios.post(
        `${API}/migrations/request`,
        migrationForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Migration request submitted! We'll review it shortly.");
      setMigrationDialogOpen(false);
      setMigrationForm({ source_url: "", source_host: "", ftp_host: "", ftp_username: "", ftp_password: "", notes: "" });
      fetchMigrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit migration request");
    } finally {
      setProcessing(false);
    }
  };

  const hasWordPressPlan = stats?.subscriptions?.some(s => s.plan_id === "wordpress" && s.status === "active");
  const statusColors = { pending: "secondary", in_progress: "default", completed: "default", failed: "destructive" };
  const ticketStatusColors = { open: "default", in_progress: "secondary", resolved: "default", closed: "secondary" };

  const handleCreateTicket = async () => {
    if (!ticketForm.subject || !ticketForm.message) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/tickets`, ticketForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Ticket created! We'll get back to you soon.");
      setTicketDialogOpen(false);
      setTicketForm({ subject: "", message: "", priority: "normal" });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ticket");
    } finally {
      setProcessing(false);
    }
  };

  const handleTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/tickets/${selectedTicket.id}/reply`, { message: ticketReply }, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedTicket(res.data);
      setTicketReply("");
      fetchTickets();
      toast.success("Reply sent!");
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Frantronics-Invoice-${invoiceId.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download invoice");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Navigation />
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">
              Welcome back, {user?.name}
            </h1>
            <p className="text-muted-foreground">
              Manage your hosting subscriptions and account settings
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-pulse text-primary">Loading dashboard...</div>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card data-testid="stats-active-subscriptions">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Active Subscriptions</CardTitle>
                    <Server className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-3xl font-bold">{stats?.active_subscriptions || 0}</div>
                  </CardContent>
                </Card>

                <Card data-testid="stats-monthly-spending">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Monthly Spending</CardTitle>
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-3xl font-bold">£{stats?.total_spending?.toFixed(2) || "0.00"}</div>
                  </CardContent>
                </Card>

                <Card data-testid="stats-account-status">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Account Status</CardTitle>
                    <Shield className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      <span className="font-heading text-lg font-semibold">Active</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Tab Navigation */}
              <div className="flex gap-2 mb-8 border-b border-border pb-2">
                <Button
                  variant={activeTab === "hosting" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab("hosting")}
                  data-testid="dashboard-tab-hosting"
                >
                  <Server className="h-4 w-4 mr-2" />
                  Hosting
                </Button>
                <Button
                  variant={activeTab === "migrations" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab("migrations")}
                  data-testid="dashboard-tab-migrations"
                >
                  <ArrowDownToLine className="h-4 w-4 mr-2" />
                  Migrations
                </Button>
                <Button
                  variant={activeTab === "support" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab("support")}
                  data-testid="dashboard-tab-support"
                >
                  <Ticket className="h-4 w-4 mr-2" />
                  Support
                </Button>
                <Button
                  variant={activeTab === "invoices" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTab("invoices")}
                  data-testid="dashboard-tab-invoices"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Invoices
                </Button>
              </div>

              {/* Hosting Tab */}
              {activeTab === "hosting" && (
                <Card data-testid="subscriptions-card">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading">Your Hosting Plans</CardTitle>
                        <CardDescription>Manage your hosting subscriptions and resources</CardDescription>
                      </div>
                      <Button onClick={() => navigate("/")} data-testid="add-subscription-btn">Add Subscription</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {stats?.subscriptions?.length > 0 ? (
                      <div className="space-y-6">
                        {stats.subscriptions.map((sub) => {
                          const planDetails = plans[sub.plan_id] || {};
                          return (
                            <div key={sub.id} className="p-6 rounded-lg border border-border hover:border-primary/30 transition-colors bg-card" data-testid={`subscription-item-${sub.id}`}>
                              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Server className="h-6 w-6 text-primary" />
                                  </div>
                                  <div>
                                    <h3 className="font-heading text-xl font-semibold">{sub.plan_name} Plan</h3>
                                    <p className="text-sm text-muted-foreground">
                                      £{sub.price}{sub.billing_period === 'annual' ? '/year' : '/month'} • {sub.status === 'cancelled' ? 'Active until: ' : 'Renews: '}
                                      {new Date(sub.expires_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Badge variant={sub.status === 'active' ? 'default' : sub.status === 'cancelled' ? 'secondary' : 'destructive'}>
                                    {sub.status}
                                  </Badge>
                                  {sub.status === 'active' && (
                                    <Button variant="outline" size="sm" onClick={() => handleCancelClick(sub)} data-testid={`cancel-subscription-${sub.id}-btn`}>
                                      Cancel
                                    </Button>
                                  )}
                                  {sub.status === 'cancelled' && (
                                    <Button size="sm" onClick={() => handleRenew(sub)} data-testid={`renew-subscription-${sub.id}-btn`}>
                                      <RefreshCw className="h-4 w-4 mr-1" />
                                      Renew
                                    </Button>
                                  )}
                                </div>
                              </div>

                              <Separator className="mb-6" />

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div className="p-4 rounded-lg bg-muted/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Database className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-medium">Storage</span>
                                  </div>
                                  <p className="font-heading text-lg font-bold">{planDetails.storage || '10 GB SSD'}</p>
                                </div>
                                <div className="p-4 rounded-lg bg-muted/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Zap className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-medium">Bandwidth</span>
                                  </div>
                                  <p className="font-heading text-lg font-bold">{planDetails.bandwidth || '100 GB'}</p>
                                </div>
                                <div className="p-4 rounded-lg bg-muted/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Globe className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-medium">Domains</span>
                                  </div>
                                  <p className="font-heading text-lg font-bold">
                                    {planDetails.domains === -1 ? 'Unlimited' : planDetails.domains || 1}
                                  </p>
                                </div>
                                <div className="p-4 rounded-lg bg-muted/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Headphones className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-medium">Support</span>
                                  </div>
                                  <p className="font-heading text-lg font-bold">{planDetails.support || 'Email'}</p>
                                </div>
                              </div>

                              <div className="p-4 rounded-lg bg-muted/30 mb-6">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <Shield className="h-4 w-4 text-primary" />
                                  Included Features
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {(planDetails.features || ['1 Website', 'Free SSL Certificate', 'Weekly Backups', '99.9% Uptime']).map((feature, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                                      <span>{feature}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {hostingPackages[sub.id] ? (
                                <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/5">
                                  <h4 className="font-medium mb-4 flex items-center gap-2 text-green-600 dark:text-green-400">
                                    <HardDrive className="h-4 w-4" />
                                    Hosting Active - {hostingPackages[sub.id].domain}
                                  </h4>
                                  
                                  {hostingPackages[sub.id].temporary_url && (
                                    <div className="mb-4 p-3 rounded bg-blue-500/10 border border-blue-500/20">
                                      <p className="text-sm text-muted-foreground mb-1">Temporary Preview URL</p>
                                      <div className="flex items-center gap-2">
                                        <a href={`https://${hostingPackages[sub.id].temporary_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline font-mono text-sm">
                                          {hostingPackages[sub.id].temporary_url}
                                        </a>
                                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(hostingPackages[sub.id].temporary_url)}>
                                          <Copy className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">This is a free preview URL. To use your own domain, purchase one from any registrar and point its nameservers to the addresses below.</p>
                                    </div>
                                  )}

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div className="space-y-3">
                                      <div>
                                        <p className="text-muted-foreground mb-1">Nameservers (Point your domain here)</p>
                                        <div className="space-y-1">
                                          {Object.entries(hostingPackages[sub.id].nameservers || {}).map(([key, value]) => (
                                            <div key={key} className="flex items-center gap-2 font-mono text-xs bg-muted px-2 py-1 rounded">
                                              <span>{value}</span>
                                              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(value)}>
                                                <Copy className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="space-y-3">
                                      <div>
                                        <p className="text-muted-foreground mb-1">FTP Details</p>
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2 font-mono text-xs bg-muted px-2 py-1 rounded">
                                            <span>Host: {hostingPackages[sub.id].ftp_host}</span>
                                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(hostingPackages[sub.id].ftp_host)}>
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                          </div>
                                          <div className="flex items-center gap-2 font-mono text-xs bg-muted px-2 py-1 rounded">
                                            <span>User: {hostingPackages[sub.id].ftp_username}</span>
                                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(hostingPackages[sub.id].ftp_username)}>
                                              <Copy className="h-3 w-3" />
                                            </Button>
                                          </div>
                                          {hostingPackages[sub.id].ftp_password && (
                                            <div className="flex items-center gap-2 font-mono text-xs bg-muted px-2 py-1 rounded">
                                              <span>Pass: ••••••••</span>
                                              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(hostingPackages[sub.id].ftp_password)}>
                                                <Copy className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {hostingPackages[sub.id].control_panel_url && (
                                        <div>
                                          <p className="text-muted-foreground mb-1">Control Panel</p>
                                          <a href={hostingPackages[sub.id].control_panel_url} target="_blank" rel="noopener noreferrer">
                                            <Button variant="outline" size="sm" className="gap-2">
                                              <ExternalLink className="h-3 w-3" />
                                              Open StackCP
                                            </Button>
                                          </a>
                                          <p className="text-xs text-muted-foreground mt-1">Login with your FTP username</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : sub.status === 'active' ? (
                                <div className="p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                    <div>
                                      <h4 className="font-medium flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                                        <AlertCircle className="h-4 w-4" />
                                        Hosting Not Set Up
                                      </h4>
                                      <p className="text-sm text-muted-foreground mt-1">
                                        Provision your hosting to get started with your website
                                      </p>
                                    </div>
                                    <Button onClick={() => handleProvisionClick(sub)} className="gap-2" data-testid={`provision-hosting-${sub.id}-btn`}>
                                      <Rocket className="h-4 w-4" />
                                      Set Up Hosting
                                    </Button>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Server className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">No active subscriptions</p>
                        <Button onClick={() => navigate("/")} data-testid="browse-plans-btn">Browse Plans</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Migrations Tab */}
              {activeTab === "migrations" && (
                <div className="space-y-8">
                  {/* DIY Migration Guide */}
                  <Card data-testid="migration-guide-card">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="font-heading flex items-center gap-2">
                            <BookOpen className="h-5 w-5 text-primary" />
                            DIY WordPress Migration Guide
                          </CardTitle>
                          <CardDescription>Follow these steps to migrate your WordPress site yourself</CardDescription>
                        </div>
                        {hasWordPressPlan && (
                          <Button onClick={() => setMigrationDialogOpen(true)} data-testid="request-migration-btn">
                            <Send className="h-4 w-4 mr-2" />
                            Request Free Migration
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {migrationGuide ? (
                        <div className="space-y-6">
                          {migrationGuide.steps.map((step) => (
                            <div key={step.step} className="flex gap-4" data-testid={`guide-step-${step.step}`}>
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                                {step.step}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium mb-1">{step.title}</h4>
                                <p className="text-sm text-muted-foreground mb-3">{step.description}</p>
                                {step.plugins && (
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {step.plugins.map((plugin) => (
                                      <div key={plugin.name} className="p-3 rounded-lg bg-muted/50 border border-border">
                                        <a href={plugin.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:underline">{plugin.name}</a>
                                        <p className="text-xs text-muted-foreground mt-1">{plugin.description}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {step.nameservers && (
                                  <div className="space-y-2">
                                    <div className="flex flex-wrap gap-2">
                                      {step.nameservers.map((ns) => (
                                        <div key={ns} className="flex items-center gap-1 font-mono text-xs bg-muted px-3 py-1.5 rounded">
                                          <span>{ns}</span>
                                          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(ns)}>
                                            <Copy className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                    {step.verification_checklist && (
                                      <div className="mt-3 p-3 rounded-lg bg-muted/30">
                                        <p className="text-sm font-medium mb-2">Verification Checklist:</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                          {step.verification_checklist.map((item, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                                              <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                                              <span>{item}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="animate-pulse text-primary">Loading guide...</div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Professional Migration - Business plan info */}
                  {!hasWordPressPlan && (
                    <Card className="border-primary/30">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Send className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-heading font-medium">Free Professional Migration</h4>
                            <p className="text-sm text-muted-foreground">Subscribe to our Business plan to get a free professional migration. We'll handle everything for you.</p>
                          </div>
                          <Button variant="outline" onClick={() => navigate("/")} data-testid="upgrade-for-migration-btn">View Business Plan</Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Migration Requests */}
                  {migrations.length > 0 && (
                    <Card data-testid="migration-requests-card">
                      <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                          <FileText className="h-5 w-5 text-primary" />
                          Your Migration Requests
                        </CardTitle>
                        <CardDescription>Track the status of your professional migration requests</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {migrations.map((mig) => (
                            <div key={mig.id} className="p-4 rounded-lg border border-border bg-card" data-testid={`migration-item-${mig.id}`}>
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                <div>
                                  <p className="font-medium">{mig.source_url}</p>
                                  <p className="text-sm text-muted-foreground">
                                    Submitted {new Date(mig.created_at).toLocaleDateString()}
                                    {mig.source_host && ` • From: ${mig.source_host}`}
                                  </p>
                                </div>
                                <Badge variant={statusColors[mig.status] || "secondary"} data-testid={`migration-status-${mig.id}`}>
                                  {mig.status.replace('_', ' ')}
                                </Badge>
                              </div>
                              {mig.admin_notes && (
                                <div className="mt-3 p-3 rounded bg-muted/50 text-sm">
                                  <p className="font-medium text-xs text-muted-foreground mb-1">Notes from our team:</p>
                                  <p>{mig.admin_notes}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Support Tab */}
              {activeTab === "support" && (
                <div className="space-y-6">
                  <Card data-testid="support-tickets-card">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="font-heading flex items-center gap-2">
                            <Ticket className="h-5 w-5 text-primary" />
                            Support Tickets
                          </CardTitle>
                          <CardDescription>Get help from our support team</CardDescription>
                        </div>
                        <Button onClick={() => setTicketDialogOpen(true)} data-testid="create-ticket-btn">
                          <Plus className="h-4 w-4 mr-2" />
                          New Ticket
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {tickets.length > 0 ? (
                        <div className="space-y-3">
                          {tickets.map((ticket) => (
                            <div
                              key={ticket.id}
                              className="p-4 rounded-lg border border-border hover:border-primary/30 transition-colors cursor-pointer bg-card"
                              onClick={() => setSelectedTicket(ticket)}
                              data-testid={`ticket-item-${ticket.id}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium">{ticket.subject}</h4>
                                <div className="flex items-center gap-2">
                                  <Badge variant={ticket.priority === "high" ? "destructive" : "outline"} className="text-xs">
                                    {ticket.priority}
                                  </Badge>
                                  <Badge variant={ticketStatusColors[ticket.status] || "secondary"}>
                                    {ticket.status.replace('_', ' ')}
                                  </Badge>
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-1">{ticket.messages[0]?.message}</p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {new Date(ticket.updated_at).toLocaleDateString()}
                                <span>•</span>
                                <MessageCircle className="h-3 w-3" />
                                {ticket.messages.length} message{ticket.messages.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground mb-4">No support tickets yet</p>
                          <Button onClick={() => setTicketDialogOpen(true)} data-testid="create-first-ticket-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Create Your First Ticket
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Invoices Tab */}
              {activeTab === "invoices" && (
                <Card data-testid="invoices-card">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Invoices & Receipts
                    </CardTitle>
                    <CardDescription>Download PDF receipts for your payments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {invoices.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice #</TableHead>
                            <TableHead>Plan</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Promo</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Download</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoices.map((inv) => (
                            <TableRow key={inv.id}>
                              <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                              <TableCell>{inv.plan_name}</TableCell>
                              <TableCell className="font-medium">£{inv.amount.toFixed(2)}</TableCell>
                              <TableCell>
                                {inv.promo_code ? (
                                  <Badge variant="secondary" className="text-xs">{inv.promo_code} ({inv.discount_percent}% off)</Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                              <TableCell>
                                <Badge variant="default">Paid</Badge>
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="sm" onClick={() => handleDownloadInvoice(inv.id)} data-testid={`download-invoice-${inv.id}`}>
                                  <ArrowDownToLine className="h-4 w-4 mr-1" />
                                  PDF
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No invoices yet. Invoices appear after successful payments.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your {selectedSubscription?.plan_name} subscription? 
              It will remain active until {selectedSubscription && new Date(selectedSubscription.expires_at).toLocaleDateString()}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Reason for cancellation (optional)</Label>
              <Textarea id="cancel-reason" placeholder="Tell us why you're cancelling..." value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Keep Subscription</Button>
            <Button variant="destructive" onClick={handleCancelConfirm} disabled={processing} data-testid="confirm-cancel-btn">
              {processing ? "Cancelling..." : "Confirm Cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provision Hosting Dialog */}
      <Dialog open={provisionDialogOpen} onOpenChange={setProvisionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary" />
              Set Up Your Hosting
            </DialogTitle>
            <DialogDescription>
              Enter your domain name to provision your {selectedSubscription?.plan_name} hosting package.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="domain-name">Domain Name</Label>
              <Input id="domain-name" placeholder="example.com" value={domainName} onChange={(e) => setDomainName(e.target.value.toLowerCase().trim())} data-testid="domain-name-input" />
              <p className="text-xs text-muted-foreground">Enter your domain without http:// or www (e.g., mywebsite.com)</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <p className="font-medium mb-2">After setup, you'll receive:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" />Nameservers to point your domain</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" />FTP credentials for file uploads</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" />Access to control panel</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-green-500" />Email with all your credentials</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProvisionDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleProvisionHosting} disabled={processing || !domainName} data-testid="confirm-provision-btn">
              {processing ? "Setting up..." : "Provision Hosting"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Migration Request Dialog */}
      <Dialog open={migrationDialogOpen} onOpenChange={setMigrationDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              Request Professional Migration
            </DialogTitle>
            <DialogDescription>
              We'll migrate your WordPress site for free. Provide the details below and our team will handle everything.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="source-url">Current Website URL *</Label>
              <Input id="source-url" placeholder="https://myoldsite.com" value={migrationForm.source_url} onChange={(e) => setMigrationForm({...migrationForm, source_url: e.target.value})} data-testid="migration-source-url-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="source-host">Current Hosting Provider</Label>
              <Input id="source-host" placeholder="e.g., GoDaddy, Bluehost, SiteGround" value={migrationForm.source_host} onChange={(e) => setMigrationForm({...migrationForm, source_host: e.target.value})} data-testid="migration-source-host-input" />
            </div>
            <Separator />
            <p className="text-sm text-muted-foreground">FTP credentials (optional - speeds up migration):</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mig-ftp-host">FTP Host</Label>
                <Input id="mig-ftp-host" placeholder="ftp.oldhost.com" value={migrationForm.ftp_host} onChange={(e) => setMigrationForm({...migrationForm, ftp_host: e.target.value})} data-testid="migration-ftp-host-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mig-ftp-user">FTP Username</Label>
                <Input id="mig-ftp-user" placeholder="username" value={migrationForm.ftp_username} onChange={(e) => setMigrationForm({...migrationForm, ftp_username: e.target.value})} data-testid="migration-ftp-user-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mig-ftp-pass">FTP Password</Label>
              <Input id="mig-ftp-pass" type="password" placeholder="••••••••" value={migrationForm.ftp_password} onChange={(e) => setMigrationForm({...migrationForm, ftp_password: e.target.value})} data-testid="migration-ftp-pass-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mig-notes">Additional Notes</Label>
              <Textarea id="mig-notes" placeholder="Any special requirements or instructions..." value={migrationForm.notes} onChange={(e) => setMigrationForm({...migrationForm, notes: e.target.value})} data-testid="migration-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrationDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitMigration} disabled={processing || !migrationForm.source_url} data-testid="submit-migration-btn">
              {processing ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Ticket className="h-5 w-5 text-primary" />
              Create Support Ticket
            </DialogTitle>
            <DialogDescription>Describe your issue and we'll get back to you as soon as possible.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ticket-subject">Subject *</Label>
              <Input id="ticket-subject" placeholder="Brief description of your issue" value={ticketForm.subject} onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})} data-testid="ticket-subject-input" />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <div className="flex gap-2">
                {["low", "normal", "high"].map((p) => (
                  <Button key={p} variant={ticketForm.priority === p ? "default" : "outline"} size="sm" onClick={() => setTicketForm({...ticketForm, priority: p})} data-testid={`ticket-priority-${p}`}>
                    {p}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ticket-message">Message *</Label>
              <Textarea id="ticket-message" placeholder="Describe your issue in detail..." rows={5} value={ticketForm.message} onChange={(e) => setTicketForm({...ticketForm, message: e.target.value})} data-testid="ticket-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTicketDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} disabled={processing || !ticketForm.subject || !ticketForm.message} data-testid="submit-ticket-btn">
              {processing ? "Creating..." : "Submit Ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{selectedTicket.subject}</DialogTitle>
                <DialogDescription className="flex items-center gap-2">
                  <Badge variant={ticketStatusColors[selectedTicket.status] || "secondary"}>{selectedTicket.status.replace('_', ' ')}</Badge>
                  <Badge variant={selectedTicket.priority === "high" ? "destructive" : "outline"}>{selectedTicket.priority}</Badge>
                  <span className="text-xs">Created {new Date(selectedTicket.created_at).toLocaleDateString()}</span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                  {selectedTicket.messages.map((msg) => (
                    <div key={msg.id} className={`p-3 rounded-lg ${msg.sender === 'admin' ? 'bg-primary/10 border border-primary/20 ml-6' : 'bg-muted/50 mr-6'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">{msg.sender === 'admin' ? 'Support Team' : msg.sender_name}</span>
                        <span className="text-xs text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  ))}
                </div>
                {selectedTicket.status !== 'closed' && (
                  <div className="flex gap-2">
                    <Textarea placeholder="Type your reply..." value={ticketReply} onChange={(e) => setTicketReply(e.target.value)} className="flex-1" data-testid="ticket-reply-input" />
                    <Button onClick={handleTicketReply} disabled={processing || !ticketReply.trim()} data-testid="send-ticket-reply-btn">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
const AdminDashboardPage = () => {
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [adminMigrations, setAdminMigrations] = useState([]);
  const [migrationUpdateOpen, setMigrationUpdateOpen] = useState(false);
  const [selectedMigration, setSelectedMigration] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState("");
  const [migrationAdminNotes, setMigrationAdminNotes] = useState("");
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [promoForm, setPromoForm] = useState({ code: "", discount_percent: 10, max_uses: null, valid_until: "", plan_ids: null });
  const [adminTickets, setAdminTickets] = useState([]);
  const [selectedAdminTicket, setSelectedAdminTicket] = useState(null);
  const [adminTicketReply, setAdminTicketReply] = useState("");

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, subsRes, migrationsRes, promosRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/subscriptions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/migrations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/promo-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/tickets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setSubscriptions(subsRes.data);
      setAdminMigrations(migrationsRes.data);
      setPromoCodes(promosRes.data);
      setAdminTickets(ticketsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User role updated to ${newRole}`);
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await axios.get(
        `${API}/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedUser(response.data);
      setUserDetailsOpen(true);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(
        `${API}/admin/users/${userToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("User deleted successfully");
      setUsers(users.filter(u => u.id !== userToDelete.id));
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      await axios.delete(
        `${API}/admin/subscriptions/${subscriptionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Subscription cancelled");
      setSubscriptions(subscriptions.map(s => 
        s.id === subscriptionId ? { ...s, status: 'cancelled' } : s
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    }
  };

  const handleMigrationUpdateClick = (migration) => {
    setSelectedMigration(migration);
    setMigrationStatus(migration.status);
    setMigrationAdminNotes(migration.admin_notes || "");
    setMigrationUpdateOpen(true);
  };

  const handleMigrationUpdate = async () => {
    if (!selectedMigration) return;
    try {
      await axios.patch(
        `${API}/admin/migrations/${selectedMigration.id}`,
        { status: migrationStatus, admin_notes: migrationAdminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Migration request updated");
      setAdminMigrations(adminMigrations.map(m => 
        m.id === selectedMigration.id ? { ...m, status: migrationStatus, admin_notes: migrationAdminNotes } : m
      ));
      setMigrationUpdateOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update migration");
    }
  };

  const handleCreatePromo = async () => {
    if (!promoForm.code || !promoForm.discount_percent) return;
    try {
      const payload = { ...promoForm, max_uses: promoForm.max_uses || null, valid_until: promoForm.valid_until || null, plan_ids: null };
      const res = await axios.post(`${API}/admin/promo-codes`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setPromoCodes([res.data, ...promoCodes]);
      setPromoDialogOpen(false);
      setPromoForm({ code: "", discount_percent: 10, max_uses: null, valid_until: "", plan_ids: null });
      toast.success("Promo code created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promo code");
    }
  };

  const handleTogglePromo = async (promo) => {
    try {
      await axios.patch(`${API}/admin/promo-codes/${promo.id}`, { active: !promo.active }, { headers: { Authorization: `Bearer ${token}` } });
      setPromoCodes(promoCodes.map(p => p.id === promo.id ? { ...p, active: !p.active } : p));
      toast.success(promo.active ? "Promo code deactivated" : "Promo code activated");
    } catch (error) {
      toast.error("Failed to update promo code");
    }
  };

  const handleDeletePromo = async (promoId) => {
    try {
      await axios.delete(`${API}/admin/promo-codes/${promoId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPromoCodes(promoCodes.filter(p => p.id !== promoId));
      toast.success("Promo code deleted");
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  const handleAdminTicketReply = async () => {
    if (!adminTicketReply.trim() || !selectedAdminTicket) return;
    try {
      const res = await axios.post(`${API}/admin/tickets/${selectedAdminTicket.id}/reply`, { message: adminTicketReply }, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedAdminTicket(res.data);
      setAdminTickets(adminTickets.map(t => t.id === res.data.id ? res.data : t));
      setAdminTicketReply("");
      toast.success("Reply sent to customer!");
    } catch (error) {
      toast.error("Failed to send reply");
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      const res = await axios.patch(`${API}/admin/tickets/${ticketId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setAdminTickets(adminTickets.map(t => t.id === ticketId ? res.data : t));
      if (selectedAdminTicket?.id === ticketId) setSelectedAdminTicket(res.data);
      toast.success(`Ticket ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      toast.error("Failed to update ticket status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Navigation />
        <div className="animate-pulse text-primary">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Navigation />
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-8 w-8 text-primary" />
              <h1 className="font-heading text-3xl font-bold tracking-tight">
                Admin Panel
              </h1>
            </div>
            <p className="text-muted-foreground">
              Manage users, subscriptions, and view platform statistics
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-8 border-b border-border pb-2">
            {["overview", "users", "subscriptions", "migrations", "promo codes", "support"].map((tab) => (
              <Button
                key={tab}
                variant={activeTab === tab ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab(tab)}
                className="capitalize"
                data-testid={`admin-tab-${tab}`}
              >
                {tab}
              </Button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-8">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card data-testid="admin-stat-users">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Users
                    </CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-3xl font-bold">{stats?.total_users || 0}</div>
                  </CardContent>
                </Card>

                <Card data-testid="admin-stat-subscriptions">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Active Subscriptions
                    </CardTitle>
                    <Server className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-3xl font-bold">{stats?.active_subscriptions || 0}</div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {stats?.cancelled_subscriptions || 0} cancelled
                    </p>
                  </CardContent>
                </Card>

                <Card data-testid="admin-stat-revenue">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Monthly Revenue
                    </CardTitle>
                    <PoundSterling className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-3xl font-bold">
                      £{stats?.monthly_revenue?.toFixed(2) || "0.00"}
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="admin-stat-total-subs">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Subscriptions
                    </CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="font-heading text-3xl font-bold">{stats?.total_subscriptions || 0}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Transactions */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Recent Transactions</CardTitle>
                  <CardDescription>Latest payment activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stats?.recent_transactions?.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="font-medium">{tx.user_email}</TableCell>
                          <TableCell>{tx.metadata?.plan_name || tx.plan_id}</TableCell>
                          <TableCell>£{tx.amount.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant={tx.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {tx.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(tx.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">All Users</CardTitle>
                <CardDescription>Manage user accounts and roles</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Subscriptions</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {u.name}
                            {u.role === 'admin' && <Crown className="h-4 w-4 text-yellow-500" />}
                          </div>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>{u.subscriptions_count}</TableCell>
                        <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewUser(u.id)}
                              data-testid={`view-user-${u.id}`}
                            >
                              <User className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Settings className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem 
                                  onClick={() => handleRoleChange(u.id, u.role === 'admin' ? 'user' : 'admin')}
                                >
                                  {u.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={() => handleDeleteClick(u)}
                                  disabled={u.id === currentUser?.id}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete User
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Subscriptions Tab */}
          {activeTab === "subscriptions" && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">All Subscriptions</CardTitle>
                <CardDescription>View and manage all customer subscriptions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{sub.user_name}</p>
                            <p className="text-sm text-muted-foreground">{sub.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{sub.plan_name}</TableCell>
                        <TableCell>£{sub.price}/mo</TableCell>
                        <TableCell>
                          <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>
                            {sub.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(sub.expires_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {sub.status === 'active' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleCancelSubscription(sub.id)}
                              data-testid={`admin-cancel-sub-${sub.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Migrations Tab */}
          {activeTab === "migrations" && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <ArrowDownToLine className="h-5 w-5 text-primary" />
                  Migration Requests
                </CardTitle>
                <CardDescription>View and manage customer WordPress migration requests</CardDescription>
              </CardHeader>
              <CardContent>
                {adminMigrations.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Source URL</TableHead>
                        <TableHead>Source Host</TableHead>
                        <TableHead>Has FTP</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adminMigrations.map((mig) => (
                        <TableRow key={mig.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{mig.user_name}</p>
                              <p className="text-sm text-muted-foreground">{mig.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{mig.source_url}</TableCell>
                          <TableCell>{mig.source_host || "-"}</TableCell>
                          <TableCell>
                            <Badge variant={mig.ftp_host ? "default" : "secondary"}>
                              {mig.ftp_host ? "Yes" : "No"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              mig.status === 'completed' ? 'default' : 
                              mig.status === 'in_progress' ? 'default' : 
                              mig.status === 'failed' ? 'destructive' : 'secondary'
                            } data-testid={`admin-migration-status-${mig.id}`}>
                              {mig.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(mig.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMigrationUpdateClick(mig)}
                              data-testid={`admin-update-migration-${mig.id}`}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <ArrowDownToLine className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No migration requests yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Promo Codes Tab */}
          {activeTab === "promo codes" && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Tag className="h-5 w-5 text-primary" />
                      Promo Codes
                    </CardTitle>
                    <CardDescription>Create and manage discount codes for customers</CardDescription>
                  </div>
                  <Button onClick={() => setPromoDialogOpen(true)} data-testid="create-promo-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Code
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {promoCodes.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Discount</TableHead>
                        <TableHead>Uses</TableHead>
                        <TableHead>Valid Until</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {promoCodes.map((promo) => (
                        <TableRow key={promo.id}>
                          <TableCell className="font-mono font-medium">{promo.code}</TableCell>
                          <TableCell>{promo.discount_percent}%</TableCell>
                          <TableCell>{promo.times_used || 0}{promo.max_uses ? ` / ${promo.max_uses}` : ''}</TableCell>
                          <TableCell>{promo.valid_until ? new Date(promo.valid_until).toLocaleDateString() : 'No expiry'}</TableCell>
                          <TableCell>
                            <Badge variant={promo.active ? "default" : "secondary"}>
                              {promo.active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleTogglePromo(promo)} data-testid={`toggle-promo-${promo.id}`}>
                                {promo.active ? "Deactivate" : "Activate"}
                              </Button>
                              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeletePromo(promo.id)} data-testid={`delete-promo-${promo.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No promo codes yet</p>
                    <Button onClick={() => setPromoDialogOpen(true)}>Create Your First Code</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Support Tab */}
          {activeTab === "support" && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-primary" />
                  Support Tickets
                </CardTitle>
                <CardDescription>Manage customer support requests</CardDescription>
              </CardHeader>
              <CardContent>
                {adminTickets.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Messages</TableHead>
                        <TableHead>Last Updated</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adminTickets.map((ticket) => (
                        <TableRow key={ticket.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ticket.user_name}</p>
                              <p className="text-sm text-muted-foreground">{ticket.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{ticket.subject}</TableCell>
                          <TableCell>
                            <Badge variant={ticket.priority === "high" ? "destructive" : "outline"}>{ticket.priority}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={ticket.status === 'open' ? 'default' : ticket.status === 'resolved' ? 'default' : 'secondary'}>
                              {ticket.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>{ticket.messages.length}</TableCell>
                          <TableCell>{new Date(ticket.updated_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => setSelectedAdminTicket(ticket)} data-testid={`admin-view-ticket-${ticket.id}`}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No support tickets yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* User Details Dialog */}
      <Dialog open={userDetailsOpen} onOpenChange={setUserDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              User Details
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{selectedUser.user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedUser.user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Role</p>
                  <Badge variant={selectedUser.user?.role === 'admin' ? 'default' : 'secondary'}>
                    {selectedUser.user?.role}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Joined</p>
                  <p className="font-medium">{new Date(selectedUser.user?.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium mb-3">Subscriptions ({selectedUser.subscriptions?.length || 0})</h4>
                {selectedUser.subscriptions?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedUser.subscriptions.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">{sub.plan_name} Plan</p>
                          <p className="text-sm text-muted-foreground">£{sub.price}{sub.billing_period === 'annual' ? '/year' : '/month'}</p>
                        </div>
                        <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>
                          {sub.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No subscriptions</p>
                )}
              </div>

              <div>
                <h4 className="font-medium mb-3">Payment History ({selectedUser.transactions?.length || 0})</h4>
                {selectedUser.transactions?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedUser.transactions.map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">£{tx.amount.toFixed(2)}</p>
                          <p className="text-sm text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</p>
                        </div>
                        <Badge variant={tx.payment_status === 'paid' ? 'default' : 'secondary'}>
                          {tx.payment_status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No payment history</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})? 
              This will permanently delete their account, subscriptions, and all associated data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Migration Update Dialog */}
      <Dialog open={migrationUpdateOpen} onOpenChange={setMigrationUpdateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ArrowDownToLine className="h-5 w-5 text-primary" />
              Update Migration Request
            </DialogTitle>
            <DialogDescription>
              Update the status and add notes for this migration request.
            </DialogDescription>
          </DialogHeader>
          {selectedMigration && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p><strong>Customer:</strong> {selectedMigration.user_name} ({selectedMigration.user_email})</p>
                <p><strong>Source:</strong> {selectedMigration.source_url}</p>
                {selectedMigration.ftp_host && (
                  <p><strong>FTP:</strong> {selectedMigration.ftp_host} / {selectedMigration.ftp_username}</p>
                )}
                {selectedMigration.notes && (
                  <p><strong>Customer notes:</strong> {selectedMigration.notes}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex gap-2 flex-wrap">
                  {["pending", "in_progress", "completed", "failed"].map((s) => (
                    <Button
                      key={s}
                      variant={migrationStatus === s ? "default" : "outline"}
                      size="sm"
                      onClick={() => setMigrationStatus(s)}
                      data-testid={`migration-status-option-${s}`}
                    >
                      {s.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-notes">Admin Notes (sent to customer)</Label>
                <Textarea
                  id="admin-notes"
                  placeholder="Add notes about the migration progress..."
                  value={migrationAdminNotes}
                  onChange={(e) => setMigrationAdminNotes(e.target.value)}
                  data-testid="migration-admin-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrationUpdateOpen(false)}>Cancel</Button>
            <Button onClick={handleMigrationUpdate} data-testid="save-migration-update-btn">
              Save & Notify Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Promo Code Dialog */}
      <Dialog open={promoDialogOpen} onOpenChange={setPromoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Tag className="h-5 w-5 text-primary" />
              Create Promo Code
            </DialogTitle>
            <DialogDescription>Create a discount code for customers to use at checkout.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="promo-code">Code *</Label>
              <Input id="promo-code" placeholder="e.g., WELCOME20" value={promoForm.code} onChange={(e) => setPromoForm({...promoForm, code: e.target.value.toUpperCase()})} data-testid="promo-code-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo-discount">Discount Percentage *</Label>
              <Input id="promo-discount" type="number" min="1" max="100" value={promoForm.discount_percent} onChange={(e) => setPromoForm({...promoForm, discount_percent: parseInt(e.target.value) || 0})} data-testid="promo-discount-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo-max-uses">Max Uses (leave empty for unlimited)</Label>
              <Input id="promo-max-uses" type="number" min="1" placeholder="Unlimited" value={promoForm.max_uses || ""} onChange={(e) => setPromoForm({...promoForm, max_uses: parseInt(e.target.value) || null})} data-testid="promo-max-uses-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo-expiry">Expiry Date (optional)</Label>
              <Input id="promo-expiry" type="date" value={promoForm.valid_until} onChange={(e) => setPromoForm({...promoForm, valid_until: e.target.value ? new Date(e.target.value).toISOString() : ""})} data-testid="promo-expiry-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePromo} disabled={!promoForm.code || !promoForm.discount_percent} data-testid="save-promo-btn">Create Code</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Ticket Detail Dialog */}
      <Dialog open={!!selectedAdminTicket} onOpenChange={(open) => !open && setSelectedAdminTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedAdminTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{selectedAdminTicket.subject}</DialogTitle>
                <DialogDescription className="flex items-center gap-2 flex-wrap">
                  <span>{selectedAdminTicket.user_name} ({selectedAdminTicket.user_email})</span>
                  <Badge variant={selectedAdminTicket.priority === "high" ? "destructive" : "outline"}>{selectedAdminTicket.priority}</Badge>
                  <Badge variant={selectedAdminTicket.status === 'open' ? 'default' : 'secondary'}>{selectedAdminTicket.status.replace('_', ' ')}</Badge>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex gap-2 flex-wrap">
                  {["open", "in_progress", "resolved", "closed"].map((s) => (
                    <Button key={s} variant={selectedAdminTicket.status === s ? "default" : "outline"} size="sm" onClick={() => handleUpdateTicketStatus(selectedAdminTicket.id, s)} data-testid={`admin-ticket-status-${s}`}>
                      {s.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
                <Separator />
                <div className="space-y-3 max-h-[35vh] overflow-y-auto">
                  {selectedAdminTicket.messages.map((msg) => (
                    <div key={msg.id} className={`p-3 rounded-lg ${msg.sender === 'admin' ? 'bg-primary/10 border border-primary/20 ml-6' : 'bg-muted/50 mr-6'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">{msg.sender === 'admin' ? 'You (Admin)' : msg.sender_name}</span>
                        <span className="text-xs text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Textarea placeholder="Reply to customer..." value={adminTicketReply} onChange={(e) => setAdminTicketReply(e.target.value)} className="flex-1" data-testid="admin-ticket-reply-input" />
                  <Button onClick={handleAdminTicketReply} disabled={!adminTicketReply.trim()} data-testid="admin-send-reply-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      <Navigation />
      <div className="pt-24 px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10">
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-3">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: February 2026</p>
          </div>

          <div className="prose prose-sm dark:prose-invert max-w-none space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">1. Introduction</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Frantronics Hosting ("we", "our", "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your personal data when you use our web hosting services.
                </p>
                <p>
                  We comply with the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018, and applicable international data protection laws including the EU GDPR where relevant.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">2. Data Controller</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Frantronics Development is the data controller responsible for your personal data. If you have any questions about this policy or our data practices, contact us at: <strong>frantronicsdevelopment@gmail.com</strong>
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">3. Data We Collect</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>We collect the following types of personal data:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Information:</strong> Full name, email address, and encrypted password when you register an account.</li>
                  <li><strong>Payment Information:</strong> Payment details are processed securely by Stripe. We do not store your full card details on our servers. We retain transaction records including amount, date, and payment status.</li>
                  <li><strong>Hosting Data:</strong> Domain names, FTP credentials, and hosting configuration data required to provision and manage your hosting packages.</li>
                  <li><strong>Usage Data:</strong> Subscription history, login activity, and service usage records.</li>
                  <li><strong>Communications:</strong> Any correspondence you send to us, including support requests.</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">4. How We Use Your Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>We use your personal data for the following purposes under the lawful bases set out by UK GDPR:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Contract Performance (Article 6(1)(b)):</strong> To provide hosting services, process payments, provision hosting packages, and manage your account.</li>
                  <li><strong>Legitimate Interests (Article 6(1)(f)):</strong> To improve our services, prevent fraud, and ensure platform security.</li>
                  <li><strong>Legal Obligation (Article 6(1)(c)):</strong> To comply with tax, accounting, and regulatory requirements.</li>
                  <li><strong>Consent (Article 6(1)(a)):</strong> To send marketing communications where you have opted in. You may withdraw consent at any time.</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">5. How We Store Your Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>Your data is stored securely using the following measures:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Encryption:</strong> Passwords are hashed using bcrypt. All data in transit is encrypted via TLS/SSL.</li>
                  <li><strong>Database Security:</strong> Data is stored in secure MongoDB databases with access restricted to authorised personnel only.</li>
                  <li><strong>Payment Security:</strong> All payment processing is handled by Stripe, a PCI DSS Level 1 certified payment processor. We never store full card numbers on our servers.</li>
                  <li><strong>Hosting Infrastructure:</strong> Hosting packages are provisioned through 20i, a UK-based hosting provider operating from UK and EU data centres.</li>
                  <li><strong>Access Controls:</strong> Administrative access is restricted with role-based authentication and JWT-secured API endpoints.</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">6. Data Retention</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>We retain your personal data only for as long as necessary:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Data:</strong> Retained while your account is active and for up to 12 months after closure.</li>
                  <li><strong>Transaction Records:</strong> Retained for 7 years to comply with UK tax and accounting regulations (HMRC requirements).</li>
                  <li><strong>Hosting Data:</strong> Deleted within 30 days of hosting package termination.</li>
                  <li><strong>Communication Records:</strong> Retained for up to 24 months for service improvement and dispute resolution.</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">7. Third-Party Data Sharing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>We share your data only with trusted third parties necessary to deliver our services:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Stripe (Payments):</strong> Processes your payment transactions securely. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe's Privacy Policy</a>.</li>
                  <li><strong>20i (Hosting):</strong> Provisions and manages your hosting packages. UK-based provider. See <a href="https://www.20i.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">20i's Privacy Policy</a>.</li>
                  <li><strong>Resend (Email):</strong> Sends transactional email notifications. See <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Resend's Privacy Policy</a>.</li>
                </ul>
                <p>We do not sell your personal data to any third party.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">8. International Data Transfers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Where your data is transferred outside the UK or EEA (for example, to service providers based in the United States), we ensure appropriate safeguards are in place, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Standard Contractual Clauses (SCCs) approved by the ICO</li>
                  <li>UK International Data Transfer Agreement (IDTA) where applicable</li>
                  <li>Adequacy decisions recognised by the UK Government</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">9. Your Rights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>Under UK GDPR, you have the following rights regarding your personal data:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Right of Access:</strong> Request a copy of the personal data we hold about you.</li>
                  <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
                  <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten").</li>
                  <li><strong>Right to Restrict Processing:</strong> Request limitation of how we process your data.</li>
                  <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format.</li>
                  <li><strong>Right to Object:</strong> Object to processing based on legitimate interests or direct marketing.</li>
                  <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
                </ul>
                <p>
                  To exercise any of these rights, contact us at <strong>frantronicsdevelopment@gmail.com</strong>. We will respond within one month as required by law.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">10. Cookies</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  We use essential cookies and local storage to maintain your login session and theme preference. These are strictly necessary for the functioning of our service and do not require consent under the Privacy and Electronic Communications Regulations (PECR).
                </p>
                <p>We do not use tracking cookies or third-party analytics cookies.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">11. Security Breaches</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  In the event of a personal data breach, we will notify the Information Commissioner's Office (ICO) within 72 hours where the breach is likely to result in a risk to individuals' rights and freedoms, as required under UK GDPR Article 33. Affected individuals will be notified without undue delay where there is a high risk.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">12. Complaints</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  If you are unhappy with how we handle your personal data, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ico.org.uk</a></li>
                  <li>Phone: 0303 123 1113</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">13. Changes to This Policy</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically. Continued use of our services after changes constitutes acceptance of the updated policy.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Server className="h-6 w-6 text-primary" />
              <span className="font-heading font-bold">Frantronics Hosting</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 Frantronics Hosting. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Chat Widget Component
const ChatWidget = () => {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", priority: "normal" });
  const [submitted, setSubmitted] = useState(false);
  const [processing, setProcessing] = useState(false);

  if (!user) return null;

  const handleSubmit = async () => {
    if (!form.subject || !form.message) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/tickets`, form, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); setOpen(false); setForm({ subject: "", message: "", priority: "normal" }); }, 2500);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed bottom-20 right-6 z-[10000]" data-testid="chat-widget">
      {open && (
        <Card className="w-80 mb-3 shadow-2xl border-primary/20 animate-in slide-in-from-bottom-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-primary" />
                Need Help?
              </CardTitle>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {submitted ? (
              <div className="text-center py-6">
                <Check className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <p className="font-medium">Message sent!</p>
                <p className="text-sm text-muted-foreground">We'll get back to you shortly.</p>
              </div>
            ) : (
              <>
                <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} data-testid="chat-subject-input" />
                <Textarea placeholder="How can we help?" rows={3} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} data-testid="chat-message-input" />
                <Button className="w-full" size="sm" onClick={handleSubmit} disabled={processing || !form.subject || !form.message} data-testid="chat-submit-btn">
                  {processing ? "Sending..." : "Send Message"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
      <Button
        className="h-14 w-14 rounded-full shadow-lg"
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-toggle"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </Button>
    </div>
  );
};

// Main App Component
// 404 Not Found Page
const NotFoundPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Navigation />
      <Card className="w-full max-w-md text-center" data-testid="not-found-card">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <AlertCircle className="h-16 w-16 text-muted-foreground" />
          </div>
          <CardTitle className="font-heading text-4xl">404</CardTitle>
          <CardDescription className="text-base mt-2">Page not found. The page you're looking for doesn't exist or has been moved.</CardDescription>
        </CardHeader>
        <CardFooter className="justify-center">
          <Button onClick={() => navigate("/")} data-testid="go-home-btn">
            Back to Homepage
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <ChatWidget />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route 
              path="/checkout/:planId" 
              element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checkout/success" 
              element={
                <ProtectedRoute>
                  <CheckoutSuccessPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checkout/cancel" 
              element={
                <ProtectedRoute>
                  <CheckoutCancelPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              } 
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
